/**
 * ExUsGrid.tsx — Ex-US Professionals V3
 * AG Grid Community wrapper for Ex-US search results.
 * Column set mirrors the v2 screenshot: Status, Name (with address),
 * Preview, Degree, Specialty, CTMS, CRM, Spend.
 * Uses AG Grid 36 "alpine" theme (modern, clean, no Semantic UI).
 */
import React, { useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry, themeAlpine } from 'ag-grid-community';
import styles from '../ExUsProfessionalsV3.module.css';
import type { ExUsSearchRowV3 } from '../../../api/exUsProfessionalsV3Api';

// Register all Community modules once
ModuleRegistry.registerModules([AllCommunityModule]);

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------

function StatusCellRenderer({ value }: ICellRendererParams) {
    const isActive = !value || value === 'A' || value === 'ACTIVE';
    return (
        <div className={styles.statusCell} title={value ?? 'Active'}>
            {isActive
                ? <span style={{ color: '#27ae60', fontSize: 18 }}>✔</span>
                : <span style={{ color: '#aaa', fontSize: 18 }}>○</span>
            }
        </div>
    );
}

interface NameCellParams extends ICellRendererParams {
    data: ExUsSearchRowV3;
    onViewDetails: (row: ExUsSearchRowV3) => void;
}

function NameCellRenderer({ data, onViewDetails }: NameCellParams) {
    if (!data) return null;
    const fullName = [data.lastName, data.firstName].filter(Boolean).join(', ').toUpperCase();
    const addressParts = [data.address1, data.city, data.state, data.countryCode].filter(Boolean);
    const addressLine  = addressParts.join(', ');

    return (
        <div className={styles.nameCell}>
            <button
                className={styles.namePrimary}
                style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
                onClick={() => onViewDetails(data)}
                data-testid="name-link"
            >
                {fullName || '—'}
            </button>
            {addressLine && (
                <div className={styles.nameSecondary}>{addressLine}</div>
            )}
        </div>
    );
}

function PreviewCellRenderer({ data, onPreview }: { data: ExUsSearchRowV3; onPreview: (row: ExUsSearchRowV3) => void } & ICellRendererParams) {
    return (
        <button
            className={styles.previewBtn}
            onClick={() => onPreview(data)}
            title="Preview"
            data-testid="btn-preview"
        >
            👁
        </button>
    );
}

function FlagCellRenderer({ value }: ICellRendererParams) {
    const isOn = value === 'Y' || value === 'y' || value === '1' || value === 'true';
    return (
        <div className={styles.flagCell}>
            <span className={isOn ? styles.flagOn : styles.flagOff} title={isOn ? 'Yes' : 'No'} />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Grid component
// ---------------------------------------------------------------------------

interface Props {
    rows:          ExUsSearchRowV3[];
    userTeam?:     string;
    onPreview:     (row: ExUsSearchRowV3) => void;
    onViewDetails: (row: ExUsSearchRowV3) => void;
}

export default function ExUsGrid({ rows, userTeam, onPreview, onViewDetails }: Props) {
    // Resolve the CRM flag column for this team (TM81/TM82 are the fallback columns)
    const crmField = useMemo((): keyof ExUsSearchRowV3 => {
        const teamNum = userTeam ? parseInt(userTeam, 10) : NaN;
        if (!isNaN(teamNum)) {
            const key = `universeFlagTm${teamNum}` as keyof ExUsSearchRowV3;
            // Only use if it's a recognised TM field
            if (key in ({} as ExUsSearchRowV3)) return key;
        }
        return 'universeFlagTm81';
    }, [userTeam]);

    const nameRenderer = useCallback((params: ICellRendererParams) => (
        <NameCellRenderer {...params} onViewDetails={onViewDetails} />
    ), [onViewDetails]);

    const previewRenderer = useCallback((params: ICellRendererParams) => (
        <PreviewCellRenderer {...params} onPreview={onPreview} />
    ), [onPreview]);

    const columnDefs = useMemo((): ColDef<ExUsSearchRowV3>[] => [
        {
            headerName:        'Status',
            field:             'status',
            width:             70,
            sortable:          true,
            cellRenderer:      StatusCellRenderer,
            headerClass:       'ag-header-center',
            cellStyle:         { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
            headerName:        'Name',
            field:             'lastName',
            flex:              2,
            minWidth:          200,
            sortable:          true,
            cellRenderer:      nameRenderer,
            comparator:        (a, b) => (a ?? '').localeCompare(b ?? ''),
        },
        {
            headerName:        'Preview',
            field:             'profId',
            width:             80,
            sortable:          false,
            cellRenderer:      previewRenderer,
            cellStyle:         { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
            headerName:        'Degree',
            field:             'degree',
            width:             90,
            sortable:          true,
        },
        {
            headerName:        'Specialty',
            field:             'specialty',
            width:             110,
            sortable:          true,
        },
        {
            headerName:        'CTMS',
            field:             'ctmsFlag',
            width:             85,
            sortable:          true,
            cellRenderer:      FlagCellRenderer,
            cellStyle:         { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
            headerName:        'CRM',
            field:             crmField,
            width:             85,
            sortable:          true,
            cellRenderer:      FlagCellRenderer,
            cellStyle:         { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
            headerName:        'Spend',
            field:             'aggspendFlag',
            width:             85,
            sortable:          true,
            cellRenderer:      FlagCellRenderer,
            cellStyle:         { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
    ], [nameRenderer, previewRenderer, crmField]);

    const defaultColDef = useMemo((): ColDef => ({
        resizable:        true,
        suppressMovable:  false,
    }), []);

    return (
        <div
            className={styles.gridWrapper}
            style={{ width: '100%' }}
            data-testid="exus-grid"
        >
            <AgGridReact<ExUsSearchRowV3>
                theme={themeAlpine}
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowHeight={52}
                headerHeight={40}
                domLayout="autoHeight"
                suppressPaginationPanel={true}
                animateRows={true}
                getRowId={(params) => String(params.data.profId ?? params.data.profIdNew ?? Math.random())}
            />
        </div>
    );
}
